/* eslint-disable no-unreachable */
import * as lib from '~/dependencies';
import api from '~/services';
import {
  log,
  openExternalURL,
  isExtension,
  getIntegrationInfo,
  integrationConfig,
  isNotAnonymous,
  isValidIntPhoneNumber,
} from '~/helpers';
import {
  standardizeContacts,
  integrationContactState,
} from '~/helpers/contactsIntegration';
import messagesToast from '~/validations/messagesNotifications';
import * as actionTypes from './actionTypes';
import CUSTOM_URLS_PNG from '../../assets/images/url.png';

const {
  SALESFORCE,
  BULLHORN,
  HUBSPOT_ID,
  ZOHO_ID,
  CUSTOM_URLS_ID,
  GO_HIGH_LEVEL_ID,
} = integrationConfig;

const INTG_PREFERENCE_KEY = 'LocalIntegrationsPreference';

const CUSTOM_URLS_INTEGRATION_INFO = {
  id: CUSTOM_URLS_ID,
  name: 'Custom Urls',
  logo: CUSTOM_URLS_PNG,
};

const timeout = ms => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

const fetchActiveIntegrations = (
  accountIntegrations,
  isIntegrated
) => async dispatch => {
  dispatch({
    type: actionTypes.FETCH_ENABLED_INTEGRATIONS_SUCCESS,
    payload: {
      accountIntegrations,
      isIntegrated,
    },
  });
};

/**
 * Reorder active integrations based on local user display preference.
 * @param {*} activeIntegrationsList active integrations list API response
 * @returns reordered active integrations list
 */
const reorderIntegrations = activeIntegrationsList => {
  const activeIntegrationsReordered = [];
  const seenIds = new Set();

  try {
    // We locally store the user preference to display integrations in a specific order
    if (localStorage.getItem(INTG_PREFERENCE_KEY)) {
      const activeIntegrationsUserPreference = JSON.parse(
        localStorage.getItem(INTG_PREFERENCE_KEY)
      );

      // Add existing items based on local data IDs
      activeIntegrationsUserPreference.forEach(userPreference => {
        const item = activeIntegrationsList.find(
          response => userPreference.id === response.id
        );
        if (item) {
          activeIntegrationsReordered.push(item);
          seenIds.add(userPreference.id);
        }
      });

      // Add any new items not present in local data
      activeIntegrationsList.forEach(item => {
        if (!seenIds.has(Number(item.id))) {
          activeIntegrationsReordered.push(item);
        }
      });

      return activeIntegrationsReordered;
    }
  } catch (err) {
    log.error(
      'Failed to handle the local integration user display preference.',
      err
    );
  }

  return activeIntegrationsReordered;
};

/**
 * Filter Go High Level integrations based on mapped numbers
 * @param {Array} activeIntegrationsList - List of active integrations
 * @param {string} outbound_cli - User's phone number
 * @returns {Array} Filtered integrations list
 */
const filterGoHighLevelForNumberMapping = async (
  activeIntegrationsList,
  outboundCli
) => {
  const filteredIntegrations = await Promise.all(
    activeIntegrationsList.map(async integration => {
      if (String(integration.id).startsWith(GO_HIGH_LEVEL_ID.toString())) {
        try {
          const mappingResponse = await api.integrations.get.goHighLevelGetMappedNumbers(
            integration.integrationAccountId
          );

          if (mappingResponse?.data) {
            // Check if any mapped number includes the user's outboundCli
            const hasMatchingNumber = mappingResponse.data.some(
              map =>
                map.number && outboundCli && outboundCli.includes(map.number)
            );

            return hasMatchingNumber ? integration : null;
          }

          return null;
        } catch (err) {
          log.error(
            `Failed to fetch mapped numbers for Go High Level integration ${integration?.integrationAccountId}:`,
            err
          );
          // In case of error, include the integration to avoid breaking functionality
          return integration;
        }
      } else {
        // Include non-Go High Level integrations as is
        return integration;
      }
    })
  );

  // Filter out null values (GHL integrations that didn't match)
  return filteredIntegrations.filter(integration => integration !== null);
};

/**
 * Fetch available info of enabled integrations for accounts.
 */
export const getConnectedIntegrations = () => async dispatch => {
  try {
    const [activeIntegrations, userInfo] = await Promise.all([
      api.integrations.get.accountIntegrations(),
      api.users.get.userInfo(),
    ]);

    const isIntegrated = !!activeIntegrations?.data?.length;
    const outboundCli = userInfo?.data?.outbound_cli;

    if (isIntegrated) {
      const activeIntegrationsList = activeIntegrations?.data.map(e => ({
        id: Number(e.id),
        name: e.name,
        logo: e.logo,
        accountId: e.account_id,
        actionUrl: e.action_url,
        contactFields: e.contact_fields,
        compatibleModeEnabled: e.compatible_mode_enabled,
        integrationAccountId: e?.integration_account_id,
      }));

      let filteredIntegrationsList;

      if (outboundCli && isValidIntPhoneNumber(outboundCli)) {
        // Filter Go High Level integrations based on mapped numbers
        filteredIntegrationsList = await filterGoHighLevelForNumberMapping(
          activeIntegrationsList,
          outboundCli
        );
      } else {
        filteredIntegrationsList = activeIntegrationsList;
      }

      log.debug(
        'filteredIntegrationsList',
        outboundCli,
        filteredIntegrationsList
      );

      if (Array.isArray(filteredIntegrationsList)) {
        filteredIntegrationsList.push(CUSTOM_URLS_INTEGRATION_INFO);
      }

      // Reordered base on user preference when exists
      const activeIntegrationsReordered = reorderIntegrations(
        filteredIntegrationsList
      );

      if (activeIntegrationsReordered.length) {
        fetchActiveIntegrations(
          activeIntegrationsReordered,
          isIntegrated
        )(dispatch);
      } else {
        fetchActiveIntegrations(
          filteredIntegrationsList,
          isIntegrated
        )(dispatch);
      }
    } else {
      fetchActiveIntegrations(
        [CUSTOM_URLS_INTEGRATION_INFO],
        isIntegrated
      )(dispatch);
    }
  } catch (err) {
    log.error('Failed to fetch integrated accounts.', err);
  }
};

/**
 * Create a mocked contact with contactState = integrationContactState.DRAFT to be display in the contact list
 * This is used when a number is not saved as contact yet to keep integration component consistent
 */
function createIntegrationMockContact({
  callId,
  integrationId,
  number,
  userName,
  accountIntegrations,
}) {
  return {
    callId,
    contactState: integrationContactState.DRAFT,
    integrationId: Number(integrationId),
    firstName: number,
    phoneNumber: number,
    companyName: null,
    ownerName: userName,
    integrationInfo: getIntegrationInfo(
      Number(integrationId),
      accountIntegrations
    ),
  };
}

/**
 * Fetch custom URLs and create a contact-like object for each URL
 * We use this approach to keep the contact list component consistent as custom URLs are also shown there
 * Custom URLs works like a contact integration but does not container all properties of a full integration
 */
const fetchCustomURLs = (callId, number) => async (dispatch, getState) => {
  const { accountIntegrations } = getState().integrations;

  if (!number || !callId) return [];

  const response = await api.integrations.get.getCustomUrl(number);

  if (!response?.data?.length) return [];

  dispatch({
    type: actionTypes.FETCH_ENABLED_INTEGRATIONS,
    payload: {
      isIntegrated: true,
    },
  });

  const customUrls = response.data.map(e => {
    // Create a contact-like object for custom URLs
    return {
      callId,
      firstName: number,
      name: e?.name,
      url: e?.url,
      integrationId: CUSTOM_URLS_ID,
      integrationInfo: getIntegrationInfo(CUSTOM_URLS_ID, accountIntegrations),
    };
  });

  return customUrls;
};

/**
 * This function requests the integrated contacts for all integrated 3-party accounts such as Hubspot, Salesforce and so on
 * Also, it format the response to standalized contact object that will be used across the application.
 * The account accountIntegrations contains the endpoints `list_contact_url` to request the contacts.
 * @param {string} callId - The current callId for an active call
 * @param {string} number - The phone number in e164 format
 * @param {string} originalCallId - The originalCallId
 */
export const getIntegratedContacts = (
  number,
  callId,
  originalCallId = null
) => async (dispatch, getState) => {
  // Clean up the existing integration contacts
  dispatch({ type: actionTypes.RESET_INTEGRATIONS_CONTACTS });

  const { user_name } = getState().auth;
  const {
    isIntegrated,
    accountIntegrations,
    contacts,
    salesforceSpecificExternalId,
  } = getState().integrations;

  let arrayContacts = [];

  try {
    const customUrls = await fetchCustomURLs(callId, number)(
      dispatch,
      getState
    );

    arrayContacts = [...customUrls];

    // Only fetch contacts if the number is not an extension and not anonymous
    const shouldFetchIntegratedContacts =
      isIntegrated && callId && !isExtension(number) && isNotAnonymous(number);

    if (shouldFetchIntegratedContacts) {
      // Fetch contacts from all integrated accounts in parallel
      await Promise.all(
        accountIntegrations
          .filter(accountInt => accountInt.id !== CUSTOM_URLS_ID)
          .map(async accountInt => {
            try {
              const { list_contact_url } = accountInt.actionUrl;
              const { integrationAccountId } = accountInt;

              if (list_contact_url) {
                // Determine which call ID to use for sync based on the account type
                const callIdSyncTo = [
                  SALESFORCE,
                  GO_HIGH_LEVEL_ID,
                ].some(integId =>
                  String(accountInt.id).startsWith(String(integId))
                )
                  ? originalCallId || callId
                  : callId;

                const contactsUri = new URL(list_contact_url);
                contactsUri.searchParams.set('phone', number);
                contactsUri.searchParams.set('call_id', callIdSyncTo);
                if (salesforceSpecificExternalId) {
                  contactsUri.searchParams.set(
                    'contact_id',
                    salesforceSpecificExternalId
                  );
                }

                const response = await api.integrations.get.contactsByUri(
                  contactsUri.toString()
                );

                if (response.data.length) {
                  let standardizedContacts = standardizeContacts(
                    response.data,
                    callId,
                    accountIntegrations,
                    number,
                    integrationAccountId
                  );

                  // Saleforce CTI Logic https://nuacom.atlassian.net/wiki/spaces/NCA/pages/2367062019/The+Calling+Custom+Protocols
                  if (
                    accountInt.id === SALESFORCE &&
                    salesforceSpecificExternalId
                  ) {
                    // Filter contacts to display only the one selected by the user via CTI
                    standardizedContacts = standardizedContacts.filter(
                      c => c.externalId === salesforceSpecificExternalId
                    );

                    if (standardizedContacts.length === 0) {
                      standardizedContacts.push(
                        createIntegrationMockContact({
                          callId,
                          integrationId: accountInt.id,
                          number,
                          userName: user_name,
                          accountIntegrations,
                        })
                      );
                    }

                    dispatch({
                      type:
                        actionTypes.SET_SALESFORCE_DISPLAY_SPECIFIC_EXTERNAL_ID,
                      externalId: null,
                    });
                  }

                  arrayContacts = [...arrayContacts, ...standardizedContacts];

                  // FIXME: DEPRECIATED
                  // HubSpot workaround. Call the API twice to update the contact owner name.
                  if (accountInt.id === HUBSPOT_ID) {
                    const hasContactOwner = arrayContacts.filter(
                      c => c.contact_owner
                    );

                    if (!hasContactOwner) {
                      await timeout(3000);

                      const hubspotContacts = await api.integrations.get.contactsByUri(
                        contactsUri.toString()
                      );

                      arrayContacts = [
                        ...arrayContacts,
                        ...standardizeContacts(
                          hubspotContacts.data,
                          callId,
                          accountIntegrations,
                          undefined,
                          integrationAccountId
                        ),
                      ];
                    }
                  }
                } else {
                  // If the integration does not return any contact, we add a mocked contact with contactState = integrationContactState.DRAFT
                  arrayContacts.push(
                    createIntegrationMockContact({
                      callId,
                      integrationId: accountInt.id,
                      number,
                      userName: user_name,
                      accountIntegrations,
                    })
                  );
                }
              }
            } catch (err) {
              log.error('Integration failed to request. Reason:', err);
            }
          })
      );
    }

    dispatch({
      type: actionTypes.FETCH_CONTACTS_INTEGRATIONS_SUCCESS,
      payload: {
        contacts: [...contacts, ...arrayContacts],
      },
    });
  } catch (err) {
    log.error('Failed to fetch integrated contacts.', err);
  }
};

/**
 * That function will create an contact on spe
 * @param {string} phoneNumber - The contact phone number to be saved
 * @param {string} callId - The callId the contacts is associate to
 * @param {string} integrationId - The integrations identitys
 * @param {integer} companyId - Company id used for the AutoTask integration
 * @param {string} name - The first name of the contact being created.
 * @param {string} contactType - The contact type
 * @param {string} observation - The contact observation
 * @param {string} company - The contact observation - Salesforce only
 */
export const createContact = (
  phoneNumber,
  callId,
  integrationId,
  companyId,
  name = '',
  lastName = '',
  contactType = '',
  observation = '',
  company = ''
) => async (dispatch, getState) => {
  const { userExtension, user_name } = getState().auth;
  const { contacts, accountIntegrations } = getState().integrations;

  try {
    if (phoneNumber && callId && integrationId) {
      const updatedContacts = lib._.cloneDeep(contacts).map(async c => {
        // Is true, it means the user just click in a "local mocked contact" that he wishes to save in the database
        // DRAFT means it's pre-saved local contact only with display purpose only
        if (
          String(c.callId) === String(callId) &&
          Number(c.integrationId) === Number(integrationId) &&
          c.contactState === integrationContactState.DRAFT
        ) {
          const accountInfo = lib._.chain(accountIntegrations)
            .filter(acc => Number(acc.id) === Number(integrationId))
            .first()
            .value();

          try {
            const createContactUrl =
              accountInfo?.actionUrl?.create_contact_url || '';
            const contactUrl = accountInfo?.actionUrl?.contact_url || '';

            const contact = await api.integrations.post.createContact(
              createContactUrl,
              {
                phone: phoneNumber,
                name,
                first_name: name,
                firstname: name,
                last_name: lastName,
                lastname: lastName,
                company_id: companyId,
                extension: userExtension, // The user extension will be setted as contact onwer
                type: contactType,
                observation,
                company,
              }
            );

            if (contact.data?.external_id) {
              const externalId = contact.data.external_id;

              let externalUrl = `${contactUrl}${externalId}`;

              c.firstName = name;
              c.ownerName = user_name;
              c.externalUrl = externalUrl;
              c.externalId = externalId && Number(externalId);
              c.contactState = integrationContactState.CREATED;

              /**
               * Handle new contact URL for SALESFORCE
               */
              if (Number(integrationId) === SALESFORCE) {
                externalUrl = contactUrl.replace('[external_id]', externalId);
                c.externalUrl = externalUrl;
                c.externalId = externalId; // Keep original format with for this integration
                /**
                 * Handle new contact URL for
                 * externalId = contact/dkCDs9Y1bspgKbwS0JT7
                 */
              } else if (
                String(integrationId).startsWith(GO_HIGH_LEVEL_ID.toString())
              ) {
                c.externalId = externalId;
                // externalId = "lead/j0TpBxIVyH6rFP4gA7SD"
                // c.integrationId = 65.1174
                const goHighLevelContactId = externalId.split('/')[1];
                externalUrl = `${contactUrl}${goHighLevelContactId}`;
                c.externalUrl = externalUrl;
                /**
                 * Handle new contact URL for BULLHORN
                 * externalId = contact/dkCDs9Y1bspgKbwS0JT7
                 */
              } else if (Number(integrationId) === BULLHORN) {
                const [
                  bullhornContactType,
                  bullhornContactId,
                ] = externalId.split('/');

                // TODO: Create composeBullhornContactUrl(externalId) function in helpers?
                externalUrl = contactUrl
                  .replace('[type]', bullhornContactType)
                  .replace('[external_id]', bullhornContactId);
                c.externalUrl = externalUrl;
                c.externalId = externalId; // Keep original format with for this integration
                /**
                 * Handle new contact URL for ZOHO_ID
                 * externalId = contact/dkCDs9Y1bspgKbwS0JT7
                 */
              } else if (Number(integrationId) === ZOHO_ID) {
                const [zohoContactType, zohoContactId] = externalId.split('/');

                externalUrl = contactUrl
                  .replace('[ContactType]', zohoContactType)
                  .replace('[ContactId]', zohoContactId);
                c.externalUrl = externalUrl;
              }

              openExternalURL(externalUrl);
            } else {
              throw new Error(messagesToast().INTEGRATIONS.createContact.error);
            }
          } catch (err) {
            lib.ToastMessage(
              'error',
              lib.i18n.t('components.integrations.integration.errorToSave'),
              6000
            );
          }
          return c;
        }
        return c;
      });

      Promise.all(updatedContacts).then(allContacts => {
        dispatch({
          type: actionTypes.FETCH_CONTACTS_INTEGRATIONS_SUCCESS,
          payload: {
            contacts: allContacts,
          },
        });
      });
    }
  } catch (err) {
    log.error(`Error to create integrated contact`, err);
  }
};

/**
 * Create a ticket inside the tickets reducer
 * @param {Array} tickets - a list of tickets
 */
export const createTicket = tickets => async dispatch => {
  dispatch({
    type: actionTypes.STORE_TICKETS_INTEGRATIONS,
    payload: {
      tickets,
    },
  });
};
